'use client';

/**
 * FeatureList - Unified feature list rendering
 * 
 * Renders a list of features with icons, titles, and descriptions.
 */

import React from 'react';
import { FeatureListConfig, AnimationConfig } from '../types';
import { SmoothReveal } from '@/components/shared/SmoothReveal';
import { SMOOTH_IN_VIEW_STAGGER } from '@/hooks/useSmoothInViewAnimation';
import { getBrandColor } from '@/config/brand-colors';
import { LucideIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

interface FeatureListProps {
  config?: FeatureListConfig;
  animation?: AnimationConfig;
  className?: string;
}

export function FeatureList({ config, animation, className = '' }: FeatureListProps) {
  if (!config || !config.features || config.features.length === 0) return null;

  const { features, layout = 'grid', columns = 2, showIcons = true, iconStyle = 'circle' } = config;

  const delay = animation?.delay || 0;
  const stagger = animation?.stagger || SMOOTH_IN_VIEW_STAGGER;

  // Get icon component from Lucide
  const getIcon = (iconName?: string): LucideIcon | null => {
    if (!iconName) return null;
    const icons = LucideIcons as unknown as Record<string, LucideIcon>;
    return icons[iconName] || null;
  };

  const iconContainerClasses: Record<string, string> = {
    circle: 'w-10 h-10 rounded-full',
    square: 'w-10 h-10 rounded-lg',
    none: '',
  };

  // List layout
  if (layout === 'list') {
    return (
      <div className={`space-y-4 ${className}`}>
        {features.map((feature, index) => {
          const Icon = getIcon(feature.icon);
          return (
            <SmoothReveal
              key={feature.title}
              delay={delay + index * stagger}
              className="flex items-start gap-4"
            >
              {showIcons && (
                <div 
                  className={`${iconContainerClasses[iconStyle]} flex items-center justify-center flex-shrink-0`}
                  style={{ backgroundColor: getBrandColor('primaryBlue') + '20' }}
                >
                  {Icon ? (
                    <Icon className="w-5 h-5" color={getBrandColor('primaryBlue')} />
                  ) : (
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getBrandColor('primaryBlue') }}
                    />
                  )}
                </div>
              )}
              <div>
                <h4 className="font-semibold" style={{ color: getBrandColor('deepNavy') }}>
                  {feature.title}
                </h4>
                {feature.description && (
                  <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                )}
              </div>
            </SmoothReveal>
          );
        })}
      </div>
    );
  }

  // Inline layout
  if (layout === 'inline') {
    return (
      <div className={`flex flex-wrap gap-4 ${className}`}>
        {features.map((feature, index) => {
          const Icon = getIcon(feature.icon);
          return (
            <SmoothReveal
              key={feature.title}
              delay={delay + index * stagger}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100"
            >
              {showIcons && Icon && (
                <Icon className="w-4 h-4" color={getBrandColor('primaryBlue')} />
              )}
              <span className="text-sm font-medium" style={{ color: getBrandColor('deepNavy') }}>
                {feature.title}
              </span>
            </SmoothReveal>
          );
        })}
      </div>
    );
  }

  // Grid layout (default)
  const columnsClass = columns === 1 ? 'grid-cols-1' :
                       columns === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                       columns === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' :
                       'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';

  return (
    <div className={`grid ${columnsClass} gap-4 ${className}`}>
      {features.map((feature, index) => {
        const Icon = getIcon(feature.icon);
        return (
          <SmoothReveal
            key={feature.title}
            delay={delay + index * stagger}
            className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100 hover:shadow-md transition-all"
          >
            {showIcons && (
              <div 
                className={`${iconContainerClasses[iconStyle]} flex items-center justify-center flex-shrink-0`}
                style={{ backgroundColor: getBrandColor('primaryBlue') + '20' }}
              >
                {Icon ? (
                  <Icon className="w-5 h-5" color={getBrandColor('primaryBlue')} />
                ) : (
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getBrandColor('primaryBlue') }}
                  />
                )}
              </div>
            )}
            <div>
              <h4 className="font-semibold" style={{ color: getBrandColor('deepNavy') }}>
                {feature.title}
              </h4>
              {feature.description && (
                <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
              )}
            </div>
          </SmoothReveal>
        );
      })}
    </div>
  );
}

export default FeatureList;
